/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { TokenType } from './Interfaces';
import { SignatureData } from './InternalInterfaces';
import { IReconnectParameters, ITrouterConnectedEventArgs, ITrouterConnectedEventArgsV4c } from './TrouterConnection';
import { FallbackReason } from './TrouterFsm';

export type Protocol = 'v4a' | 'v4c';

export function usedProtocol(tokenType: TokenType, reconnectParams: IReconnectParameters | undefined): Protocol {
    return (tokenType === 'skype' && safeToString(reconnectParams?.scae) !== '1') ? 'v4a' : 'v4c';
}

function safeToString(value: unknown) {
    if (typeof value === 'string') {
        return value;
    } else if (typeof value === 'number') {
        return value.toString();
    }
    return undefined;
}

export function usedProtocolAfterFallback(protocol: Protocol, fallbackReason?: FallbackReason): Protocol {
    if (fallbackReason?.kind === 'v4c-websocket-failure') {
        return 'v4a';
    }

    return protocol;
}

export function isV4ConnectEvent(args: ITrouterConnectedEventArgs | ITrouterConnectedEventArgsV4c) {
    return Object.prototype.hasOwnProperty.call(args, 'connectparams');
}

/**
 * Needed because we want to have cache with reconnectUrl, serviceUrl working
 * independent of protocol (see IReconnectParameters and its uses)
 */
export function adaptUrl(url: string, protocol: Protocol): string {
    if (protocol === 'v4a') {
        // The insecure (http://, ws://) versions would only ever be used by a test application,
        // the connection URL is normally determined by the consumer of the library.
        // eslint-disable-next-line @microsoft/sdl/no-insecure-url
        return url.replace(/\/v4\/c\b/, '/v4/a').replace('wss://', 'https://').replace('ws://', 'http://');
    } else {
        // eslint-disable-next-line @microsoft/sdl/no-insecure-url
        return url.replace(/\/v4\/a\b/, '/v4/c').replace('https://', 'wss://').replace('http://', 'ws://');
    }
}

/**
 * Unfortunately the pre-existing cache format doesn't separate connectparams into its own property so it could be easily sent back,
 * instead it manually removes `reconnectUrl` and `serviceUrl` and send everything else, so we need to do the same here
 */
export function reconnectParamsWithoutUrls(reconnectParams: IReconnectParameters | undefined) {
    if (reconnectParams === undefined) {
        return undefined;
    }

    return <Partial<SignatureData>>{ ...reconnectParams, reconnectUrl: undefined, serviceUrl: undefined };
}

export function ensureNumber(value: string | number): number {
    return typeof value === 'string' ? parseInt(value, 10) : value;
}

export function redirectUrlIfPresent(reason: FallbackReason | undefined): string | undefined {
    if (reason?.kind !== 'redirect' || reason.host === undefined) {
        return undefined;
    }

    let redirectHost = reason.host;
    if (redirectHost.endsWith('/')) {
        redirectHost = redirectHost.substring(0, redirectHost.length - 1);
    }

    if (!redirectHost.endsWith('/v4/c') && !redirectHost.endsWith('/v4/a')) {
        redirectHost += '/v4/c';
    }

    return redirectHost;
}



// WEBPACK FOOTER //
// ./lib_temp/ProtocolAdapters.ts